import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Lightbulb, RotateCcw, Check, X, ChevronLeft, ChevronRight, Trophy, 
  Calendar, Zap, Brain, Target, Loader2, Sparkles 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LearningBytesPage = () => {
  const [bytes, setBytes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('flashcards');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [quizScore, setQuizScore] = useState({ correct: 0, total: 0 });
  const [quizComplete, setQuizComplete] = useState(false);

  useEffect(() => {
    fetchBytes();
  }, []);

  const fetchBytes = async () => {
    try {
      const response = await axios.get(`${API}/learning-bytes`);
      setBytes(response.data);
    } catch (error) {
      console.error('Error fetching bytes:', error);
      toast.error('Failed to load learning bytes');
    } finally {
      setLoading(false);
    }
  };

  const flashcards = bytes.filter(b => b.byte_type === 'flashcard');
  const quizzes = bytes.filter(b => b.byte_type === 'quiz');
  const dueForReview = bytes.filter(b => b.next_review && new Date(b.next_review) <= new Date());
  const mastered = bytes.filter(b => b.mastery_level >= 3);

  const handleMastery = async (byteId, level) => {
    const intervals = { hard: 1, medium: 3, easy: 7 };
    const nextReview = addDays(new Date(), intervals[level]);
    
    try {
      // Would need backend endpoint for updating mastery
      toast.success(`Marked as ${level}! Review in ${intervals[level]} day(s)`);
      
      // Move to next card
      if (currentIndex < flashcards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setFlipped(false);
      }
    } catch (error) {
      console.error('Error updating mastery:', error);
    }
  };

  const resetQuiz = () => {
    setCurrentIndex(0);
    setShowAnswer(false);
    setQuizScore({ correct: 0, total: 0 });
    setQuizComplete(false);
  };

  const FlashcardViewer = ({ cards }) => {
    if (cards.length === 0) return (
      <Card className="p-12 text-center  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
        <Lightbulb className="w-16 h-16 mx-auto mb-4 text-[var(--isp-muted)]" />
        <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No flashcards yet</h3>
        <p className="text-[var(--isp-body)] mb-4">Generate flashcards from your lectures!</p>
        <Button 
          onClick={() => window.location.href = '/lectures'}
          className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500"
        >
          Go to Lectures
        </Button>
      </Card>
    );

    const current = cards[currentIndex % cards.length];

    return (
      <div className="space-y-6">
        {/* Progress */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[var(--isp-body)] text-sm">Card</span>
            <span className="text-[var(--isp-heading)] font-bold">{currentIndex + 1}</span>
            <span className="text-[var(--isp-body)] text-sm">of {cards.length}</span>
          </div>
          <div className="flex gap-2">
            <Button aria-label="Previous"
              size="icon"
              variant="outline"
              onClick={() => { setCurrentIndex(Math.max(0, currentIndex - 1)); setFlipped(false); }}
              disabled={currentIndex === 0}
              className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button aria-label="Next"
              size="icon"
              variant="outline"
              onClick={() => { setCurrentIndex(Math.min(cards.length - 1, currentIndex + 1)); setFlipped(false); }}
              disabled={currentIndex === cards.length - 1}
              className="rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Flashcard */}
        <div className="perspective-1000">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex + (flipped ? '-flipped' : '')}
              initial={{ rotateY: 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: -90, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setFlipped(!flipped)}
              className="cursor-pointer"
            >
              <Card className={`p-8 sm:p-12 min-h-[350px] flex items-center justify-center  border-2 transition-all duration-300 ${
                flipped 
                  ? 'bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border-purple-500/30' 
                  : 'bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)] hover:border-purple-500/30'
              }`}>
                <div className="text-center max-w-lg">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6 ${
                    flipped 
                      ? 'bg-purple-500/20 text-purple-300' 
                      : 'bg-white/10 text-[var(--isp-body)]'
                  }`}>
                    {flipped ? (
                      <>
                        <Sparkles className="w-3 h-3" />
                        ANSWER
                      </>
                    ) : (
                      <>
                        <Brain className="w-3 h-3" />
                        QUESTION
                      </>
                    )}
                  </div>
                  <p className="text-xl sm:text-2xl font-medium text-white leading-relaxed">
                    {flipped ? current.content.answer || 'Answer' : current.content.question || 'Question'}
                  </p>
                  <p className="mt-6 text-sm text-[var(--isp-muted)]">
                    {flipped ? 'Click to see question' : 'Click to reveal answer'}
                  </p>
                </div>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mastery buttons */}
        {flipped && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-3 gap-3"
          >
            <Button
              className="h-auto py-4 flex-col gap-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300"
              onClick={() => handleMastery(current.id, 'hard')}
            >
              <X className="w-5 h-5" />
              <span className="text-sm font-medium">Hard</span>
              <span className="text-xs opacity-70">Review tomorrow</span>
            </Button>
            <Button
              className="h-auto py-4 flex-col gap-2 rounded-xl bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 text-orange-300"
              onClick={() => handleMastery(current.id, 'medium')}
            >
              <RotateCcw className="w-5 h-5" />
              <span className="text-sm font-medium">Medium</span>
              <span className="text-xs opacity-70">Review in 3 days</span>
            </Button>
            <Button
              className="h-auto py-4 flex-col gap-2 rounded-xl bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-300"
              onClick={() => handleMastery(current.id, 'easy')}
            >
              <Check className="w-5 h-5" />
              <span className="text-sm font-medium">Easy</span>
              <span className="text-xs opacity-70">Review in 7 days</span>
            </Button>
          </motion.div>
        )}
      </div>
    );
  };

  const QuizViewer = ({ quizzes }) => {
    const [selectedAnswer, setSelectedAnswer] = useState(null);

    if (quizzes.length === 0) return (
      <Card className="p-12 text-center  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
        <Target className="w-16 h-16 mx-auto mb-4 text-[var(--isp-muted)]" />
        <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No quizzes yet</h3>
        <p className="text-[var(--isp-body)] mb-4">Generate quizzes from your lectures!</p>
        <Button 
          onClick={() => window.location.href = '/lectures'}
          className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500"
        >
          Go to Lectures
        </Button>
      </Card>
    );

    if (quizComplete) {
      const percentage = Math.round((quizScore.correct / quizScore.total) * 100);
      return (
        <Card className="p-8 sm:p-12 text-center  bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-purple-500/30">
          <Trophy className={`w-20 h-20 mx-auto mb-4 ${percentage >= 70 ? 'text-yellow-400' : 'text-[var(--isp-muted)]'}`} />
          <h2 className="text-3xl font-bold text-[var(--isp-heading)] mb-2">Quiz Complete!</h2>
          <p className="text-4xl font-bold text-purple-400 mb-4">{percentage}%</p>
          <p className="text-[var(--isp-body)] mb-6">
            You got {quizScore.correct} out of {quizScore.total} questions correct
          </p>
          <Button onClick={resetQuiz} className="rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
            Try Again
          </Button>
        </Card>
      );
    }

    const current = quizzes[currentIndex];

    const handleSelectAnswer = (index) => {
      if (showAnswer) return;
      setSelectedAnswer(index);
      setShowAnswer(true);
      
      const isCorrect = index === current.content.correct;
      setQuizScore(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1
      }));
    };

    const handleNextQuestion = () => {
      if (currentIndex >= quizzes.length - 1) {
        setQuizComplete(true);
      } else {
        setCurrentIndex(currentIndex + 1);
        setShowAnswer(false);
        setSelectedAnswer(null);
      }
    };

    return (
      <div className="space-y-6">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--isp-body)]">Question {currentIndex + 1} of {quizzes.length}</span>
            <span className="text-purple-400 font-medium">{quizScore.correct} correct</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / quizzes.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question card */}
        <Card className="p-6 sm:p-8  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <h3 className="text-xl sm:text-2xl font-medium text-[var(--isp-heading)] mb-8 leading-relaxed">
            {current.content.question || 'Quiz question'}
          </h3>

          <div className="space-y-3">
            {(current.content.options || ['A', 'B', 'C', 'D']).map((option, idx) => (
              <motion.button
                key={idx}
                onClick={() => handleSelectAnswer(idx)}
                className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
                  showAnswer
                    ? idx === current.content.correct
                      ? 'bg-green-500/20 border-2 border-green-500/50 text-green-300'
                      : selectedAnswer === idx
                      ? 'bg-red-500/20 border-2 border-red-500/50 text-red-300'
                      : 'bg-[var(--isp-glass-inner)] border-2 border-transparent text-[var(--isp-body)]'
                    : 'bg-[var(--isp-glass-inner)] border-2 border-transparent text-white hover:bg-slate-700 hover:border-purple-500/30'
                }`}
                disabled={showAnswer}
                whileHover={{ scale: showAnswer ? 1 : 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                  showAnswer && idx === current.content.correct
                    ? 'bg-green-500/30 text-green-300'
                    : showAnswer && selectedAnswer === idx
                    ? 'bg-red-500/30 text-red-300'
                    : 'bg-white/10 text-[var(--isp-body)]'
                }`}>
                  {String.fromCharCode(65 + idx)}
                </div>
                <span className="flex-1">{option}</span>
                {showAnswer && idx === current.content.correct && (
                  <Check className="w-6 h-6 text-green-400" />
                )}
                {showAnswer && selectedAnswer === idx && idx !== current.content.correct && (
                  <X className="w-6 h-6 text-red-400" />
                )}
              </motion.button>
            ))}
          </div>

          {showAnswer && current.content.explanation && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-purple-500/10 rounded-xl border border-purple-500/20"
            >
              <p className="text-sm text-purple-300">
                <span className="font-semibold">Explanation:</span> {current.content.explanation}
              </p>
            </motion.div>
          )}

          {showAnswer && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6"
            >
              <Button
                className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500"
                onClick={handleNextQuestion}
              >
                {currentIndex >= quizzes.length - 1 ? 'See Results' : 'Next Question'}
              </Button>
            </motion.div>
          )}
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="learning-bytes-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight">
          Learning Bytes
        </h1>
        <p className="text-[var(--isp-body)] mt-2 text-base sm:text-lg">
          Master your knowledge with AI-powered flashcards and quizzes
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 sm:p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Lightbulb className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{bytes.length}</p>
              <p className="text-xs text-[var(--isp-body)] uppercase tracking-wide">Total Bytes</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{dueForReview.length}</p>
              <p className="text-xs text-[var(--isp-body)] uppercase tracking-wide">Due Review</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{mastered.length}</p>
              <p className="text-xs text-[var(--isp-body)] uppercase tracking-wide">Mastered</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6  bg-[var(--isp-glass)] backdrop-blur-xl border-[var(--isp-glass-border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{quizzes.length}</p>
              <p className="text-xs text-[var(--isp-body)] uppercase tracking-wide">Quizzes</p>
            </div>
          </div>
        </Card>
      </div>

      {/* View mode tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <Button
          variant={viewMode === 'flashcards' ? 'default' : 'outline'}
          onClick={() => { setViewMode('flashcards'); setCurrentIndex(0); setFlipped(false); }}
          className={`rounded-xl gap-2 ${
            viewMode === 'flashcards'
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
              : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
          }`}
        >
          <Brain className="w-4 h-4" />
          Flashcards ({flashcards.length})
        </Button>
        <Button
          variant={viewMode === 'quizzes' ? 'default' : 'outline'}
          onClick={() => { setViewMode('quizzes'); resetQuiz(); }}
          className={`rounded-xl gap-2 ${
            viewMode === 'quizzes'
              ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
              : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
          }`}
        >
          <Target className="w-4 h-4" />
          Quiz Mode ({quizzes.length})
        </Button>
        <Button
          variant={viewMode === 'review' ? 'default' : 'outline'}
          onClick={() => { setViewMode('review'); setCurrentIndex(0); setFlipped(false); }}
          className={`rounded-xl gap-2 ${
            viewMode === 'review'
              ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
              : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Due for Review ({dueForReview.length})
        </Button>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto">
        {viewMode === 'flashcards' && <FlashcardViewer cards={flashcards} />}
        {viewMode === 'quizzes' && <QuizViewer quizzes={quizzes} />}
        {viewMode === 'review' && <FlashcardViewer cards={dueForReview} />}
      </div>
    </div>
  );
};

export default LearningBytesPage;
